/**
 * Phase F11 FE-2a — ID document capture step.
 *
 * Sits between :class:`ProctorConsentGate` (camera/mic permission grant)
 * and :class:`StartGate` (start the timer). Captures a one-shot photo
 * of the applicant's photo ID and uploads it through the F11 BE-1 mint
 * → PUT → commit pipeline before letting the applicant start.
 *
 * The F12 ML cheat-detection slice cross-references this still against
 * the periodic webcam stills (F11 FE-2b) to flag identity mismatches —
 * F11 FE-2a only captures + uploads.
 *
 * Capture pipeline:
 *   1. Open `getUserMedia({ video: { facingMode: "environment" } })` so
 *      laptops with a single front camera fall back gracefully and
 *      phones with a back camera prefer it (sharper for ID scans).
 *   2. Render a live preview with a framing rectangle.
 *   3. On "Capture": draw the current video frame onto an offscreen
 *      canvas at 1280x960 (or the camera's native size if smaller),
 *      export as JPEG @ q=0.85.
 *   4. Show the still + Retake / Use this photo controls.
 *   5. On "Use this photo": run `proctor-client.uploadArtifact` (mint
 *      → PUT → commit). On success, call `onIdCaptured()` so the
 *      parent advances to StartGate.
 *
 * Failure modes:
 *   * Permission denied / hardware unavailable / browser unsupported →
 *     same per-error copy taxonomy as :class:`ProctorConsentGate`.
 *     Applicant can retry indefinitely; the gate doesn't auto-advance.
 *   * Upload failure → keep the still in memory + render an inline
 *     retry button. The applicant can also "Retake" to discard and
 *     start over.
 *
 * Privacy: the camera stream is stopped as soon as the still is taken
 * (we don't keep it hot during the review-and-confirm step). The
 * captured blob lives in memory only until upload completes.
 */
import * as React from "react";
import { Button } from "@eduviaos/ui-kit";
import { motion } from "motion/react";
import { AlertTriangle, Camera, Check, Loader2, RotateCcw, ShieldX } from "lucide-react";

import { PageCard } from "@/components/AppFrame";
import type { ProctorClient } from "@/lib/proctor-client";

type CaptureState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "previewing"; stream: MediaStream }
  | { kind: "review"; blob: Blob; previewUrl: string }
  | { kind: "uploading"; blob: Blob; previewUrl: string }
  | { kind: "uploaded"; previewUrl: string }
  | { kind: "denied"; reason: CaptureDeniedReason }
  | { kind: "upload_failed"; blob: Blob; previewUrl: string; message: string };

type CaptureDeniedReason =
  | "permission_denied"
  | "hardware_unavailable"
  | "unsupported_browser"
  | "transient_error";

const _DENIED_COPY: Record<CaptureDeniedReason, string> = {
  permission_denied:
    "We couldn't access your camera. Please grant permission in your browser, then try again.",
  hardware_unavailable:
    "We couldn't find a working camera. Plug one in (or pick a different device in your browser settings) and try again.",
  unsupported_browser:
    "Your browser doesn't support camera capture. Please use the latest Chrome, Edge, Safari, or Firefox.",
  transient_error:
    "Something went wrong while opening your camera. Please try again — if the problem persists, restart the browser.",
};

export interface IdDocumentCaptureProps {
  client: ProctorClient;
  onIdCaptured: () => void;
}

export function IdDocumentCapture({ client, onIdCaptured }: IdDocumentCaptureProps) {
  const [state, setState] = React.useState<CaptureState>({ kind: "idle" });
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const previewUrlRef = React.useRef<string | null>(null);

  // Always release tracks + revoke object URLs on unmount.
  React.useEffect(() => {
    return () => {
      stopStream(streamRef.current);
      streamRef.current = null;
      revokePreview(previewUrlRef.current);
      previewUrlRef.current = null;
    };
  }, []);

  // When state transitions into `previewing`, attach the stream to the
  // <video> element. We can't do this declaratively because srcObject
  // is a non-idempotent property — assigning the same stream twice on a
  // re-render kicks off a fresh `loadedmetadata`.
  React.useEffect(() => {
    if (state.kind !== "previewing") return;
    if (!videoRef.current) return;
    videoRef.current.srcObject = state.stream;
    videoRef.current.play().catch(() => undefined);
  }, [state]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const startCamera = React.useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setState({ kind: "denied", reason: "unsupported_browser" });
      return;
    }
    setState({ kind: "starting" });
    try {
      // Prefer the back camera for sharper ID scans. Browsers fall back
      // to the front camera silently when no env-facing device exists.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 960 }, facingMode: "environment" },
        audio: false,
      });
      streamRef.current = stream;
      setState({ kind: "previewing", stream });
    } catch (err) {
      const reason = classifyMediaError(err);
      setState({ kind: "denied", reason });
    }
  }, []);

  const captureStill = React.useCallback(async () => {
    if (state.kind !== "previewing") return;
    const video = videoRef.current;
    if (!video) return;

    const blob = await drawVideoFrameToJpeg(video);
    if (!blob) {
      setState({
        kind: "upload_failed",
        blob: new Blob(),
        previewUrl: "",
        message:
          "Couldn't capture the still — your browser blocked the canvas readback. Please retry.",
      });
      return;
    }
    // Stop the live stream now — we don't need it during the
    // review-and-confirm step.
    stopStream(streamRef.current);
    streamRef.current = null;

    const previewUrl = URL.createObjectURL(blob);
    revokePreview(previewUrlRef.current);
    previewUrlRef.current = previewUrl;

    setState({ kind: "review", blob, previewUrl });
  }, [state]);

  const retake = React.useCallback(() => {
    revokePreview(previewUrlRef.current);
    previewUrlRef.current = null;
    setState({ kind: "idle" });
    void startCamera();
  }, [startCamera]);

  const uploadStill = React.useCallback(async () => {
    if (state.kind !== "review" && state.kind !== "upload_failed") return;
    const blob = state.blob;
    const previewUrl = state.previewUrl;
    setState({ kind: "uploading", blob, previewUrl });
    try {
      await client.uploadArtifact({
        blob,
        kind: "id_document",
        mimeType: "image/jpeg",
        capturedMetadata: {
          width: 1280,
          height: 960,
        },
      });
      setState({ kind: "uploaded", previewUrl });
      onIdCaptured();
    } catch (err) {
      const message = errorMessageOf(err);
      setState({ kind: "upload_failed", blob, previewUrl, message });
    }
  }, [state, client, onIdCaptured]);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <PageCard
      title="Verify your identity"
      subtitle="Hold a government-issued photo ID up to the camera and capture a clear shot. We compare it to your webcam during the assessment."
    >
      <CaptureBody
        state={state}
        videoRef={videoRef}
        onStart={startCamera}
        onCapture={captureStill}
        onRetake={retake}
        onUpload={uploadStill}
      />

      {state.kind === "denied" && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{_DENIED_COPY[state.reason]}</span>
        </div>
      )}

      {state.kind === "upload_failed" && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{state.message}</span>
        </div>
      )}
    </PageCard>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

interface CaptureBodyProps {
  state: CaptureState;
  videoRef: React.RefObject<HTMLVideoElement>;
  onStart: () => void;
  onCapture: () => void;
  onRetake: () => void;
  onUpload: () => void;
}

function CaptureBody({
  state,
  videoRef,
  onStart,
  onCapture,
  onRetake,
  onUpload,
}: CaptureBodyProps) {
  return (
    <div className="mt-5 grid gap-4">
      <div className="bg-muted/40 grid place-items-center rounded-md p-4">
        <div className="relative w-full max-w-md">
          {state.kind === "previewing" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.18 }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="aspect-[4/3] w-full rounded border bg-black object-cover"
                aria-label="Camera preview"
              />
              <div
                aria-hidden
                className="border-primary/80 pointer-events-none absolute inset-4 rounded-md border-2 border-dashed"
              />
            </motion.div>
          )}

          {(state.kind === "review" ||
            state.kind === "uploading" ||
            state.kind === "uploaded" ||
            state.kind === "upload_failed") && (
            <motion.img
              src={state.previewUrl}
              alt="Captured ID document"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.18 }}
              className="aspect-[4/3] w-full rounded border bg-black object-cover"
            />
          )}

          {(state.kind === "idle" || state.kind === "starting" || state.kind === "denied") && (
            <div
              className="text-muted-foreground flex aspect-[4/3] w-full flex-col items-center justify-center rounded border border-dashed text-sm"
              aria-hidden
            >
              {state.kind === "starting" ? (
                <>
                  <Loader2 className="text-primary mb-2 h-6 w-6 animate-spin" />
                  <span>Opening your camera…</span>
                </>
              ) : state.kind === "denied" ? (
                <>
                  <ShieldX className="mb-2 h-6 w-6" />
                  <span>Permission required</span>
                </>
              ) : (
                <>
                  <Camera className="mb-2 h-6 w-6" />
                  <span>Camera preview will appear here</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <CaptureActions
        state={state}
        onStart={onStart}
        onCapture={onCapture}
        onRetake={onRetake}
        onUpload={onUpload}
      />
    </div>
  );
}

interface CaptureActionsProps {
  state: CaptureState;
  onStart: () => void;
  onCapture: () => void;
  onRetake: () => void;
  onUpload: () => void;
}

function CaptureActions({ state, onStart, onCapture, onRetake, onUpload }: CaptureActionsProps) {
  if (state.kind === "idle" || state.kind === "denied") {
    const unsupported = state.kind === "denied" && state.reason === "unsupported_browser";
    return (
      <div className="flex justify-end">
        <Button type="button" onClick={onStart} disabled={unsupported} size="lg">
          {state.kind === "denied" ? "Try again" : "Open camera"}
        </Button>
      </div>
    );
  }

  if (state.kind === "starting") {
    return (
      <div className="flex justify-end">
        <Button type="button" disabled size="lg">
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          Starting…
        </Button>
      </div>
    );
  }

  if (state.kind === "previewing") {
    return (
      <div className="flex justify-end">
        <Button type="button" onClick={onCapture} size="lg">
          <Camera className="mr-1.5 h-4 w-4" />
          Capture photo
        </Button>
      </div>
    );
  }

  if (state.kind === "review") {
    return (
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onRetake} size="lg">
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Retake
        </Button>
        <Button type="button" onClick={onUpload} size="lg">
          <Check className="mr-1.5 h-4 w-4" />
          Use this photo
        </Button>
      </div>
    );
  }

  if (state.kind === "upload_failed") {
    return (
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onRetake} size="lg">
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Retake
        </Button>
        <Button type="button" onClick={onUpload} size="lg">
          <Check className="mr-1.5 h-4 w-4" />
          Retry upload
        </Button>
      </div>
    );
  }

  if (state.kind === "uploading") {
    return (
      <div className="flex justify-end">
        <Button type="button" disabled size="lg">
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          Uploading…
        </Button>
      </div>
    );
  }

  // uploaded — the parent flips us out of view immediately, but render
  // a frozen success state for the briefest of moments.
  return (
    <div className="flex justify-end">
      <Button type="button" disabled size="lg">
        <Check className="mr-1.5 h-4 w-4" />
        Uploaded
      </Button>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function stopStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
}

function revokePreview(url: string | null): void {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

async function drawVideoFrameToJpeg(video: HTMLVideoElement): Promise<Blob | null> {
  const w = video.videoWidth || 1280;
  const h = video.videoHeight || 960;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    ctx.drawImage(video, 0, 0, w, h);
  } catch {
    // Tainted canvas — happens when a stream comes from a cross-origin
    // source. With getUserMedia it shouldn't, but guard anyway.
    return null;
  }
  return await canvasToBlob(canvas, "image/jpeg", 0.85);
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function classifyMediaError(err: unknown): CaptureDeniedReason {
  if (err === null || typeof err !== "object" || !("name" in err)) {
    return "transient_error";
  }
  const name = String((err as { name: unknown }).name);
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "permission_denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "hardware_unavailable";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "transient_error";
    default:
      return "transient_error";
  }
}

function errorMessageOf(err: unknown): string {
  if (err === null || err === undefined) return "Unknown upload error.";
  if (typeof err === "string") return err;
  if (typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Couldn't upload your ID document. Please retry.";
}
